import React, { useEffect, useState } from 'react';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import {
  Box,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import { PageCard } from '@components/common/PageCard';
import { mailingService } from '@services/mailing.service';
import { toastService } from '@services/toast.service';
import {
  ClientMailingsResponse,
  MailingChannel,
  MailingSubscription,
  MailingType,
  MAILING_TYPE_LABELS,
  MAILING_TYPES,
} from '../../types/mailing.types';

interface Props {
  clientId: string;
}

const getEnabled = (subscriptions: MailingSubscription[] | undefined, type: MailingType, channel: MailingChannel): boolean =>
  (subscriptions ?? []).find(s => s.type === type && s.channel === channel)?.enabled ?? false;

export const ClientMailingsTab: React.FC<Props> = ({ clientId }) => {
  const [data, setData] = useState<ClientMailingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null); // `${type}:${channel}`

  useEffect(() => {
    mailingService.getClientMailings(clientId)
      .then(setData)
      .catch(() => toastService.error('Не вдалося завантажити налаштування розсилок.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const handleToggle = async (type: MailingType, channel: MailingChannel, currentEnabled: boolean) => {
    if (!data) return;

    const key = `${type}:${channel}`;
    setToggling(key);

    // Оптимістичне оновлення
    setData(prev => {
      if (!prev) return prev;
      const exists = prev.subscriptions.find(s => s.type === type && s.channel === channel);
      const updated = exists
        ? prev.subscriptions.map(s => s.type === type && s.channel === channel ? { ...s, enabled: !currentEnabled } : s)
        : [...prev.subscriptions, { type, channel, enabled: true }];
      return { ...prev, subscriptions: updated };
    });

    try {
      if (currentEnabled) {
        await mailingService.unsubscribe(clientId, type, channel);
        toastService.success('Відписано від розсилки.');
      } else {
        await mailingService.subscribe(clientId, type, channel);
        toastService.success('Підписано на розсилку.');
      }
    } catch {
      // Відкат при помилці
      setData(prev => {
        if (!prev) return prev;
        const updated = prev.subscriptions.map(s =>
          s.type === type && s.channel === channel ? { ...s, enabled: currentEnabled } : s
        );
        return { ...prev, subscriptions: updated };
      });
      toastService.error('Не вдалося змінити налаштування розсилки.');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  return (
    <PageCard data-i-doc={PageIndices.EXPERT_CLIENT_MAILINGS}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Керуйте каналами отримання повідомлень для цього клієнта. Зміни застосовуються одразу.
      </Typography>

      {!data.hasTelegramConnected && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
          <TelegramIcon fontSize="small" />
          <Typography variant="body2">
            Telegram не підключено — канал недоступний.
          </Typography>
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Вид розсилки</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Telegram</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MAILING_TYPES.map(type => (
              <TableRow key={type}>
                <TableCell>{MAILING_TYPE_LABELS[type]}</TableCell>
                {(['email', 'telegram'] as MailingChannel[]).map(channel => {
                  const enabled = getEnabled(data.subscriptions, type, channel);
                  const key = `${type}:${channel}`;
                  const isTelegramDisabled = channel === 'telegram' && !data.hasTelegramConnected;

                  return (
                    <TableCell key={channel} align="center">
                      <Tooltip title={isTelegramDisabled ? 'Telegram не підключено' : ''} disableHoverListener={!isTelegramDisabled}>
                        <span>
                          <Switch
                            checked={enabled}
                            disabled={isTelegramDisabled || toggling === key}
                            onChange={() => handleToggle(type, channel, enabled)}
                            color="primary"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PageCard>
  );
};
